import {
  DOCUMENT_ASSEMBLER_LIMITS,
  IDocumentAssemblerTemplate,
  IDocumentAssemblerTemplateCategory,
  updateDocumentAssemblerTemplate,
} from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  Textarea,
} from '@commenda/kakashi';
import { messageFromApiErrorResponse } from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../hooks/useNotification';
import { DocumentAssemblerCategoryPicker } from './DocumentAssemblerCategoryPicker';

interface DocumentAssemblerTemplateEditDialogProps {
  template: IDocumentAssemblerTemplate;
  onClose: () => void;
  onUpdated?: () => void;
}

export const DocumentAssemblerTemplateEditDialog = ({
  template,
  onClose,
  onUpdated,
}: DocumentAssemblerTemplateEditDialogProps) => {
  const { setNotification } = useNotification();

  const [title, setTitle] = useState(template.title);
  const [description, setDescription] = useState(template.description);
  const [categoryKey, setCategoryKey] = useState<string | null>(
    template.categories[0]?.key ?? null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const hasChanged =
    trimmedTitle !== template.title ||
    trimmedDescription !== template.description ||
    categoryKey !== template.categories[0]?.key;
  // A template's description cannot be cleared: the server declares it non-empty when present.
  const canSave =
    Boolean(trimmedTitle) &&
    Boolean(trimmedDescription) &&
    Boolean(categoryKey) &&
    hasChanged &&
    !isSaving;

  const onSubmit = async () => {
    if (!categoryKey) return;

    setSubmitError(null);
    setIsSaving(true);

    const response = await updateDocumentAssemblerTemplate(template.id, {
      title: trimmedTitle,
      description: trimmedDescription,
      categories: [
        categoryKey,
        ...template.categories.slice(1).map((category) => category.key),
      ],
    });

    if (!response.ok) {
      setSubmitError(
        await messageFromApiErrorResponse(
          response,
          'The template could not be updated.'
        )
      );
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    setNotification({ show: true, valid: true, title: 'Template updated' });
    onUpdated?.();
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="flex-col items-start gap-xs">
          <DialogTitle>Edit template</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-xl p-3xl">
          {submitError ? (
            <Alert variant="destructive" description={submitError} />
          ) : null}

          <FieldWrapper>
            <Label htmlFor="document-assembler-edit-template-key">Key</Label>
            <Input
              id="document-assembler-edit-template-key"
              value={template.key}
              disabled
            />
            <p className="txt-extra-small-regular mt-xs text-text-secondary">
              A template’s key never changes.
            </p>
          </FieldWrapper>

          <FieldWrapper>
            <Label htmlFor="document-assembler-edit-template-name" required>
              Name
            </Label>
            <Input
              id="document-assembler-edit-template-name"
              value={title}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxNameLength}
              onChange={(event) => setTitle(event.target.value)}
            />
          </FieldWrapper>

          <DocumentAssemblerCategoryPicker
            selectedCategoryKey={categoryKey ?? ''}
            onSelect={(category: IDocumentAssemblerTemplateCategory | null) =>
              setCategoryKey(category?.key ?? null)
            }
            label="Category"
            isRequired
            canCreate
            isInsideDialog
          />

          <FieldWrapper>
            <Label
              htmlFor="document-assembler-edit-template-description"
              required
            >
              Description
            </Label>
            <Textarea
              id="document-assembler-edit-template-description"
              rows={4}
              value={description}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxDescriptionLength}
              onChange={(event) => setDescription(event.target.value)}
            />
            <p className="txt-extra-small-regular mt-xs text-text-secondary">
              The expert agent picks a template by reading this. Say what it
              covers <strong>and what it does not</strong>.
            </p>
          </FieldWrapper>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" size="sm" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            type="button"
            disabled={!canSave}
            isLoading={isSaving}
            onClick={onSubmit}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
